import { Color3 } from '@babylonjs/core';
import type { RoadClass } from '@roadworld/shared';

/**
 * Knobs for AI traffic. Slices 1-7 of the plan progressively use more of this; values are
 * present from slice 1 so the file shape doesn't churn between slices.
 *
 * Distance units = meters, speed units = m/s internally (km/h in comments for readability),
 * angles = radians.
 */
export const TrafficTuning = {
  // --- Chassis (matches the player car so collisions feel consistent) ---
  chassisWidth: 1.8,
  chassisHeight: 1.0,
  chassisLength: 4.2,
  /** Lighter than player (1400 kg) so a player T-bone shoves the AI car visibly. */
  chassisMass: 1000,
  wheelRadius: 0.4,
  wheelWidth: 0.3,

  // --- Physics behavior ---
  /** Baseline linear damping. Higher than the player's 0.05 because AI cars have no wheel
   *  controllers — air drag substitutes for some of what wheels provide. */
  linearDamping: 0.3,
  /** Resists yaw spinning from collisions; tuned with MAX_YAW_RATE so steering still bites. */
  angularDamping: 1.5,
  /** Cuboid friction with terrain/buildings. */
  friction: 0.4,
  restitution: 0.0,

  // --- Drive forces (slice 1: simple speed-PI controller) ---
  /** Impulse magnitude per (m/s of speed error) per second when accelerating (N). */
  speedGain: 6000,
  /** Maximum forward thrust impulse-rate (N). Caps acceleration so cars don't blast off. */
  thrustImpulseMaxRate: 8000,
  /** Maximum braking impulse-rate (N). */
  brakeImpulseMaxRate: 12000,

  // --- Steering (slice 2+) ---
  /** Pure-pursuit lookahead distance along the path. */
  lookaheadMeters: 15,
  /** Multiplier turning yaw error (rad) → desired angular velocity (rad/s). */
  turnGain: 2.0,
  /** Maximum yaw angular velocity (rad/s). Caps how fast cars can rotate. */
  maxYawRateRadS: 1.5,
  /** Yaw-torque impulse-rate per (rad/s of angvel error) (N·m). Empirically scaled by mass·dt. */
  yawInertiaScale: 1500,
  /** Lateral velocity damping factor (1/sec). Replaces wheel side friction; without it
   *  AI cars slide outward in turns. */
  lateralDamp: 4.0,

  // --- Speed by road class (slice 3+) ---
  /** Target speed in km/h per road class. Unlisted classes fall back to `defaultSpeedKmh`. */
  roadClassSpeedKmh: {
    motorway: 110,
    motorway_link: 60,
    trunk: 100,
    trunk_link: 55,
    primary: 80,
    primary_link: 45,
    secondary: 60,
    secondary_link: 40,
    tertiary: 45,
    tertiary_link: 35,
    residential: 35,
    unclassified: 40,
    living_street: 20,
    service: 25,
    track: 20,
    road: 40,
  } as Partial<Record<RoadClass, number>>,
  defaultSpeedKmh: 30,
  /** ±this fraction random offset per car. 0.1 = ±10%. */
  speedJitterFraction: 0.1,

  // --- Spawn weighting by road class (slice 3+) ---
  /** Higher = more cars on this class. Used both for spawn position picking AND for
   *  weighting next-edge choice at intersections, so highway-following dominates. */
  spawnWeight: {
    motorway: 8,
    motorway_link: 6,
    trunk: 7,
    trunk_link: 5,
    primary: 5,
    primary_link: 3,
    secondary: 3,
    secondary_link: 2,
    tertiary: 2,
    tertiary_link: 1.5,
    residential: 1,
    unclassified: 1.2,
    living_street: 0.5,
    service: 0.5,
    track: 0.25,
    road: 1,
  } as Partial<Record<RoadClass, number>>,
  defaultSpawnWeight: 0.5,

  // --- Density (slice 3+) ---
  /** Target average cars per active chunk. 3×3 active ring → ~27 cars total. */
  carsPerActiveChunkBudget: 9,
  /** Hard cap per chunk regardless of weight. */
  maxCarsPerChunk: 18,
  /** Reference road-weight used to normalize per-chunk density. Calibrate empirically. */
  referenceChunkWeight: 3000,
  /** Spawn attempts before giving up on a slot (rejecting if too close to other cars). */
  maxSpawnAttempts: 5,
  /** Minimum spawn distance from the player and other cars (m). */
  minSpawnSeparation: 30,

  // --- Lane positioning (slice 4) ---
  /** true = right-hand traffic (US, mainland Europe), false = left-hand traffic (UK, JP).
   *  Default location is Llanberis (Wales, UK), so default to LEFT. */
  driveOnRight: false,
  /** Fraction of halfWidth to offset from centerline. 0.5 = centered in the right/left lane. */
  laneOffsetFraction: 0.5,

  // --- Intersection yield (slice 5) ---
  /** Distance to end-node at which a car attempts to reserve the intersection (m). */
  intersectionLookaheadM: 12,
  /** Distance below which a yielding car comes to a near-stop instead of crawling (m). */
  intersectionStopDistM: 4,
  /** Crawl speed for a yielding car (m/s). ~5 km/h. */
  yieldCrawlSpeedMs: 1.4,
  /** Reservation timeout — release valve for cars that get stuck mid-intersection (sec). */
  intersectionReservationSec: 2.0,

  // --- Obstacle awareness (slice 6) ---
  /** Forward raycast length (m). */
  obstacleRayLengthM: 8,
  /** Distance at which obstacles trigger slowdown (m). */
  obstacleSlowdownDistM: 6,
  /** Distance at which obstacles trigger full stop (m). */
  obstacleStopDistM: 2,
  /** Raycast frequency per car (Hz). */
  obstacleRayHz: 5,
  /** Blocked time before despawn (sec). */
  blockedDespawnSec: 5,
  /** Off-road time before despawn (sec). */
  offRoadDespawnSec: 3,

  // --- Visual ---
  /** Muted color palette — picked at random per car. Avoids cartoon-bright primaries. */
  bodyColors: [
    new Color3(0.78, 0.16, 0.18), // muted red
    new Color3(0.2, 0.3, 0.55), // navy
    new Color3(0.85, 0.85, 0.85), // off-white
    new Color3(0.12, 0.12, 0.13), // near-black
    new Color3(0.55, 0.55, 0.58), // silver
    new Color3(0.3, 0.45, 0.3), // forest green
    new Color3(0.55, 0.4, 0.25), // tan/brown
    new Color3(0.4, 0.4, 0.55), // slate blue
    new Color3(0.7, 0.6, 0.2), // ochre
    new Color3(0.5, 0.18, 0.4), // muted plum
  ],
} as const;

/** Returns the per-class target speed in m/s (with ±jitter applied), default if unlisted. */
export function targetSpeedMs(roadClass: RoadClass, rng: () => number): number {
  const kmh = TrafficTuning.roadClassSpeedKmh[roadClass] ?? TrafficTuning.defaultSpeedKmh;
  const jitter = 1 + (rng() * 2 - 1) * TrafficTuning.speedJitterFraction;
  return (kmh * jitter) / 3.6;
}

/** Returns the per-class spawn weight, falling back to defaultSpawnWeight. */
export function spawnWeight(roadClass: RoadClass): number {
  return TrafficTuning.spawnWeight[roadClass] ?? TrafficTuning.defaultSpawnWeight;
}

/** Picks a random body color from the palette. */
export function pickBodyColor(rng: () => number): Color3 {
  const palette = TrafficTuning.bodyColors;
  return palette[Math.floor(rng() * palette.length)] ?? palette[0]!;
}
